import React, { useState, useEffect, useCallback, useMemo } from "react";
import Sidebar from "./Navbar";
import { BASE_URL } from "./constants";

const Payouts = () => {
  const admin = JSON.parse(localStorage.getItem("admin") || "{}");

  const [withdrawals, setWithdrawals] = useState([]);
  const [stats, setStats] = useState({
    pendingCount: 0,
    pendingAmount: 0,
    approvedCount: 0,
    approvedAmount: 0,
    rejectedCount: 0,
    rejectedAmount: 0,
    totalCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("ALL"); // ALL | PENDING | APPROVED | REJECTED
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Modals state
  const [approveModalItem, setApproveModalItem] = useState(null);
  const [referenceIdInput, setReferenceIdInput] = useState("");
  const [approveNotesInput, setApproveNotesInput] = useState("");
  const [approving, setApproving] = useState(false);

  const [rejectModalItem, setRejectModalItem] = useState(null);
  const [failureReasonInput, setFailureReasonInput] = useState("");
  const [rejectNotesInput, setRejectNotesInput] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const [detailModalItem, setDetailModalItem] = useState(null);

  // Toast notification state
  const [toast, setToast] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const copyToClipboard = (text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    showToast(`Copied "${text}" to clipboard`, "info");
    setTimeout(() => setCopiedId(null), 2500);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    window.location.href = "/login";
  };

  // Fetch withdrawals from backend
  const fetchWithdrawals = useCallback(
    async (isManual = false) => {
      if (isManual) setRefreshing(true);
      else setLoading(true);

      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const params = new URLSearchParams();
        if (activeTab !== "ALL") params.append("status", activeTab);
        if (searchQuery.trim()) params.append("search", searchQuery.trim());
        params.append("page", currentPage);
        params.append("limit", 20);

        const res = await fetch(
          `${BASE_URL}admin/account/withdrawals?${params.toString()}`,
          { headers }
        );
        const json = await res.json();

        if (json.status === "success" && json.data) {
          setWithdrawals(json.data.withdrawals || []);
          if (json.data.stats) setStats(json.data.stats);
          if (json.data.pagination) {
            setTotalPages(json.data.pagination.totalPages || 1);
            setTotalItems(json.data.pagination.totalItems || 0);
          }
        } else {
          showToast(json.message || "Failed to load withdrawal records", "error");
        }
      } catch (err) {
        console.error("Error fetching withdrawals:", err);
        showToast("Network error while loading payouts", "error");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [activeTab, searchQuery, currentPage]
  );

  useEffect(() => {
    fetchWithdrawals();
  }, [fetchWithdrawals]);

  // Handle Approve
  const handleConfirmApprove = async () => {
    if (!approveModalItem) return;
    if (!referenceIdInput.trim()) {
      showToast("Please enter a Bank UTR / Reference ID", "error");
      return;
    }

    setApproving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}admin/account/withdrawals/${approveModalItem.id}/approve`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            referenceId: referenceIdInput.trim(),
            notes: approveNotesInput.trim(),
          }),
        }
      );
      const json = await res.json();

      if (json.status === "success") {
        showToast(
          `Payout of ₹${approveModalItem.amount.toLocaleString(
            "en-IN"
          )} approved and marked transferred`,
          "success"
        );
        setApproveModalItem(null);
        setReferenceIdInput("");
        setApproveNotesInput("");
        fetchWithdrawals(true);
      } else {
        showToast(json.message || "Approval failed", "error");
      }
    } catch (err) {
      console.error("Approve error:", err);
      showToast("Network error approving payout", "error");
    } finally {
      setApproving(false);
    }
  };

  // Handle Reject & Auto-Refund
  const handleConfirmReject = async () => {
    if (!rejectModalItem) return;
    if (!failureReasonInput.trim()) {
      showToast("Please enter a reason for rejecting this request", "error");
      return;
    }

    setRejecting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}admin/account/withdrawals/${rejectModalItem.id}/reject`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            failureReason: failureReasonInput.trim(),
            notes: rejectNotesInput.trim(),
          }),
        }
      );
      const json = await res.json();

      if (json.status === "success") {
        showToast(
          `Request rejected. ₹${rejectModalItem.amount.toLocaleString(
            "en-IN"
          )} refunded to buddy's wallet.`,
          "success"
        );
        setRejectModalItem(null);
        setFailureReasonInput("");
        setRejectNotesInput("");
        fetchWithdrawals(true);
      } else {
        showToast(json.message || "Rejection failed", "error");
      }
    } catch (err) {
      console.error("Reject error:", err);
      showToast("Network error rejecting payout", "error");
    } finally {
      setRejecting(false);
    }
  };

  // Export current list to CSV
  const handleExportCSV = () => {
    if (!withdrawals.length) {
      showToast("No records available to export", "error");
      return;
    }

    const headers = [
      "ID",
      "Requested Date",
      "Buddy Name",
      "Buddy Phone",
      "Bank Account Name",
      "Account Number",
      "IFSC Code",
      "Amount (INR)",
      "Status",
      "UTR / Ref ID",
      "Failure Reason",
      "Processed Date",
    ];

    const rows = withdrawals.map((w) => [
      w.id,
      new Date(w.requestedAt).toLocaleString("en-IN"),
      `"${w.provider?.name || "N/A"}"`,
      `"${w.provider?.phone || "N/A"}"`,
      `"${w.payoutAccount?.accountHolderName || "N/A"}"`,
      `"${w.payoutAccount?.accountNumber || "N/A"}"`,
      `"${w.payoutAccount?.ifscCode || "N/A"}"`,
      w.amount || 0,
      w.status,
      `"${w.razorpayReferenceId || ""}"`,
      `"${w.failureReason || ""}"`,
      w.processedAt ? new Date(w.processedAt).toLocaleString("en-IN") : "",
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `inb_payouts_${new Date().toISOString().split("T")[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast("Payout records exported successfully", "success");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (val) => {
    return `₹${Number(val || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-[#1C1917] flex flex-col font-sans">
      <Sidebar admin={admin} onLogout={handleLogout} />

      {/* Main Container with lg:ml-72 so it is NOT hidden under the fixed sidebar */}
      <main className="lg:ml-72 min-h-screen flex flex-col flex-1 pb-16">
        {/* Toast Notification Banner */}
        {toast && (
          <div
            className={`fixed top-5 right-5 z-50 px-4 py-3 rounded-xl shadow-lg flex items-center space-x-3 text-xs font-bold transition-all ${
              toast.type === "success"
                ? "bg-stone-950 text-emerald-400 border border-emerald-500/30"
                : toast.type === "error"
                ? "bg-stone-950 text-rose-400 border border-rose-500/30"
                : "bg-stone-950 text-amber-300 border border-amber-500/30"
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-current"></span>
            <span>{toast.message}</span>
          </div>
        )}

        {/* Sticky Top Header */}
        <header className="bg-white border-b border-stone-200 px-4 sm:px-6 lg:px-8 py-5 sticky top-0 z-30 shadow-xs">
          <div className="pt-16 lg:pt-0 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
            <div>
              <div className="flex items-center space-x-2 text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1">
                <span>Finance</span>
                <span>/</span>
                <span className="text-stone-900 font-bold">Payouts & Withdrawals</span>
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold tracking-tight text-stone-900 flex items-center gap-3">
                <span>Payouts & Wallet Withdrawals</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200">
                  <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                  <span>Settlement Hub</span>
                </span>
              </h1>
              <p className="text-sm text-stone-500 mt-1">
                Process buddy earnings disbursements, verify destination bank accounts, and monitor transfer history.
              </p>
            </div>

            <div className="flex items-center space-x-3 self-start xl:self-auto">
              <button
                onClick={handleExportCSV}
                className="inline-flex items-center px-3.5 py-2 text-xs font-bold rounded-xl bg-white border border-stone-300 text-stone-700 hover:bg-stone-50 hover:border-stone-400 transition-all shadow-xs"
                title="Export filtered records to CSV"
              >
                <svg
                  className="w-4 h-4 mr-2 text-stone-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Export CSV
              </button>

              <button
                onClick={() => fetchWithdrawals(true)}
                disabled={refreshing}
                className="inline-flex items-center px-4 py-2 text-xs font-bold rounded-xl bg-stone-900 text-white hover:bg-stone-800 transition-all shadow-xs disabled:opacity-50"
              >
                <svg
                  className={`w-4 h-4 mr-2 text-[#F8D346] ${refreshing ? "animate-spin" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                {refreshing ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>
        </header>

        {/* Content Body */}
        <div className="px-4 sm:px-6 lg:px-8 pt-6 space-y-6">

          {/* 4 KPI Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card 1: Pending Queue */}
            <div className="bg-white border border-stone-200/80 rounded-xl p-5 shadow-sm hover:border-amber-400 transition-all">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">
                  Pending Verification
                </span>
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </span>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold text-stone-900">
                  {formatCurrency(stats.pendingAmount)}
                </div>
                <div className="flex items-center space-x-1.5 mt-1 text-xs text-stone-500">
                  <span className="font-semibold text-amber-700">
                    {stats.pendingCount}
                  </span>
                  <span>requests in queue</span>
                </div>
              </div>
            </div>

            {/* Card 2: Disbursed Total */}
            <div className="bg-white border border-stone-200/80 rounded-xl p-5 shadow-sm hover:border-emerald-400 transition-all">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">
                  Total Disbursed
                </span>
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </span>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold text-stone-900">
                  {formatCurrency(stats.approvedAmount)}
                </div>
                <div className="flex items-center space-x-1.5 mt-1 text-xs text-stone-500">
                  <span className="font-semibold text-emerald-700">
                    {stats.approvedCount}
                  </span>
                  <span>successful transfers</span>
                </div>
              </div>
            </div>

            {/* Card 3: Rejected / Refunded */}
            <div className="bg-white border border-stone-200/80 rounded-xl p-5 shadow-sm hover:border-rose-400 transition-all">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">
                  Rejected & Refunded
                </span>
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-rose-50 text-rose-700 border border-rose-200">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </span>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold text-stone-900">
                  {formatCurrency(stats.rejectedAmount)}
                </div>
                <div className="flex items-center space-x-1.5 mt-1 text-xs text-stone-500">
                  <span className="font-semibold text-rose-700">
                    {stats.rejectedCount}
                  </span>
                  <span>returned to wallet</span>
                </div>
              </div>
            </div>

            {/* Card 4: Total Volume */}
            <div className="bg-white border border-stone-200/80 rounded-xl p-5 shadow-sm hover:border-stone-400 transition-all">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">
                  All-Time Volume
                </span>
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-stone-100 text-stone-700 border border-stone-200">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </span>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold text-stone-900">
                  {formatCurrency(
                    stats.pendingAmount +
                      stats.approvedAmount +
                      stats.rejectedAmount
                  )}
                </div>
                <div className="flex items-center space-x-1.5 mt-1 text-xs text-stone-500">
                  <span className="font-semibold text-stone-700">
                    {stats.totalCount}
                  </span>
                  <span>lifetime requests</span>
                </div>
              </div>
            </div>
          </div>

          {/* Filters, Tabs & Search Bar */}
          <div className="bg-white border border-stone-200 rounded-xl p-4 shadow-sm space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Segmented Status Tabs */}
              <div className="flex items-center space-x-1 p-1 bg-stone-100 rounded-lg overflow-x-auto text-xs font-semibold">
                <button
                  onClick={() => {
                    setActiveTab("ALL");
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md transition-all whitespace-nowrap ${
                    activeTab === "ALL"
                      ? "bg-white text-stone-900 shadow-sm"
                      : "text-stone-600 hover:text-stone-900"
                  }`}
                >
                  All ({stats.totalCount})
                </button>
                <button
                  onClick={() => {
                    setActiveTab("PENDING");
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md transition-all whitespace-nowrap flex items-center space-x-1.5 ${
                    activeTab === "PENDING"
                      ? "bg-white text-amber-700 shadow-sm"
                      : "text-stone-600 hover:text-stone-900"
                  }`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                  <span>Pending ({stats.pendingCount})</span>
                </button>
                <button
                  onClick={() => {
                    setActiveTab("APPROVED");
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md transition-all whitespace-nowrap flex items-center space-x-1.5 ${
                    activeTab === "APPROVED"
                      ? "bg-white text-emerald-700 shadow-sm"
                      : "text-stone-600 hover:text-stone-900"
                  }`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  <span>Approved ({stats.approvedCount})</span>
                </button>
                <button
                  onClick={() => {
                    setActiveTab("REJECTED");
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md transition-all whitespace-nowrap flex items-center space-x-1.5 ${
                    activeTab === "REJECTED"
                      ? "bg-white text-rose-700 shadow-sm"
                      : "text-stone-600 hover:text-stone-900"
                  }`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                  <span>Rejected ({stats.rejectedCount})</span>
                </button>
              </div>

              {/* Search Bar */}
              <div className="relative flex-1 md:max-w-md">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search by Buddy name, phone, account, IFSC..."
                  className="w-full pl-9 pr-8 py-2 text-xs bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white text-stone-900"
                />
                <svg
                  className="w-4 h-4 absolute left-3 top-2.5 text-stone-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2.5 top-2.5 text-stone-400 hover:text-stone-600"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Withdrawals Table */}
          <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-stone-50/80 border-b border-stone-200 text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                    <th className="py-3 px-4">Buddy Details</th>
                    <th className="py-3 px-4">Destination Bank Account</th>
                    <th className="py-3 px-4">Amount & Balance</th>
                    <th className="py-3 px-4">Requested Date</th>
                    <th className="py-3 px-4">Status & Details</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100 text-xs">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-stone-400">
                        <div className="inline-flex items-center space-x-2">
                          <svg
                            className="w-5 h-5 animate-spin text-stone-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                          </svg>
                          <span>Loading withdrawal requests...</span>
                        </div>
                      </td>
                    </tr>
                  ) : withdrawals.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-16 text-center">
                        <div className="max-w-sm mx-auto space-y-3">
                          <div className="w-12 h-12 mx-auto rounded-full bg-stone-100 text-stone-400 flex items-center justify-center">
                            <svg
                              className="w-6 h-6"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                              />
                            </svg>
                          </div>
                          <div className="text-sm font-semibold text-stone-800">
                            No withdrawal requests found
                          </div>
                          <p className="text-xs text-stone-500">
                            {searchQuery
                              ? `No results match your search "${searchQuery}". Try clearing the search query.`
                              : activeTab !== "ALL"
                              ? `There are currently no requests with status "${activeTab}".`
                              : "When buddies request wallet withdrawals, they will appear here for verification and transfer."}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    withdrawals.map((item) => {
                      const isPending = item.status === "PENDING";
                      const isApproved = item.status === "APPROVED";
                      const isRejected = item.status === "REJECTED";

                      return (
                        <tr
                          key={item.id}
                          className="hover:bg-stone-50/70 transition-colors"
                        >
                          {/* 1. Buddy Details */}
                          <td className="py-3.5 px-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-9 h-9 rounded-full bg-stone-900 text-[#F8D346] flex items-center justify-center font-bold text-xs shrink-0 overflow-hidden">
                                {item.provider?.profileImage ? (
                                  <img
                                    src={item.provider.profileImage}
                                    alt={item.provider?.name || "Buddy"}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  (item.provider?.name || "B")
                                    .charAt(0)
                                    .toUpperCase()
                                )}
                              </div>
                              <div>
                                <a
                                  href={`/admin/provider/${item.providerId}`}
                                  className="font-medium text-stone-900 hover:text-amber-600 transition-colors block"
                                >
                                  {item.provider?.name || "Unknown Buddy"}
                                </a>
                                <div className="text-[11px] text-stone-500 flex items-center space-x-1.5 mt-0.5">
                                  <span>{item.provider?.phone || "No Phone"}</span>
                                  {item.provider?.averageRating > 0 && (
                                    <>
                                      <span>•</span>
                                      <span className="text-amber-600 font-semibold">
                                        ★ {item.provider.averageRating.toFixed(1)}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* 2. Destination Bank Account */}
                          <td className="py-3.5 px-4">
                            {item.payoutAccount ? (
                              <div className="space-y-1">
                                <div className="font-semibold text-stone-900 text-xs">
                                  {item.payoutAccount.accountHolderName}
                                </div>
                                <div className="flex items-center space-x-2 text-[11px] text-stone-600">
                                  <span className="font-mono">
                                    {item.payoutAccount.accountNumber}
                                  </span>
                                  <button
                                    onClick={() =>
                                      copyToClipboard(
                                        item.payoutAccount.accountNumber,
                                        `acc-${item.id}`
                                      )
                                    }
                                    className="text-stone-400 hover:text-stone-700 p-0.5 rounded"
                                    title="Copy Account Number"
                                  >
                                    <svg
                                      className="w-3.5 h-3.5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                      />
                                    </svg>
                                  </button>
                                </div>
                                <div className="flex items-center space-x-2 text-[11px] text-stone-500">
                                  <span className="font-mono font-medium">
                                    IFSC: {item.payoutAccount.ifscCode}
                                  </span>
                                  <button
                                    onClick={() =>
                                      copyToClipboard(
                                        item.payoutAccount.ifscCode,
                                        `ifsc-${item.id}`
                                      )
                                    }
                                    className="text-stone-400 hover:text-stone-700 p-0.5 rounded"
                                    title="Copy IFSC Code"
                                  >
                                    <svg
                                      className="w-3.5 h-3.5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <span className="text-stone-400 text-xs italic">
                                No bank account linked
                              </span>
                            )}
                          </td>

                          {/* 3. Amount & Wallet Balance */}
                          <td className="py-3.5 px-4">
                            <div className="font-bold text-stone-900 text-sm">
                              {formatCurrency(item.amount)}
                            </div>
                            <div className="text-[11px] text-stone-500 mt-0.5">
                              Wallet: {formatCurrency(item.wallet?.balance || 0)}
                            </div>
                          </td>

                          {/* 4. Requested Date */}
                          <td className="py-3.5 px-4 text-stone-600">
                            <div>{formatDate(item.requestedAt)}</div>
                            <div className="text-[10px] text-stone-400 font-mono mt-0.5">
                              ID: #{item.id.slice(-6)}
                            </div>
                          </td>

                          {/* 5. Status & Details */}
                          <td className="py-3.5 px-4">
                            {isPending && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-1.5"></span>
                                Pending Review
                              </span>
                            )}
                            {isApproved && (
                              <div className="space-y-0.5">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5"></span>
                                  Transferred
                                </span>
                                {item.razorpayReferenceId && (
                                  <div className="text-[10px] text-stone-500 font-mono">
                                    Ref: {item.razorpayReferenceId}
                                  </div>
                                )}
                              </div>
                            )}
                            {isRejected && (
                              <div className="space-y-0.5">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-50 text-rose-800 border border-rose-200">
                                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mr-1.5"></span>
                                  Rejected
                                </span>
                                {item.failureReason && (
                                  <div className="text-[10px] text-rose-600 truncate max-w-xs">
                                    {item.failureReason}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>

                          {/* 6. Actions */}
                          <td className="py-3.5 px-4 text-right">
                            {isPending ? (
                              <div className="inline-flex items-center space-x-2">
                                <button
                                  onClick={() => {
                                    setApproveModalItem(item);
                                    setReferenceIdInput("");
                                    setApproveNotesInput("");
                                  }}
                                  className="px-2.5 py-1.5 text-xs font-semibold rounded bg-stone-900 text-white hover:bg-stone-800 transition-all shadow-sm"
                                >
                                  Approve & Pay
                                </button>
                                <button
                                  onClick={() => {
                                    setRejectModalItem(item);
                                    setFailureReasonInput("");
                                    setRejectNotesInput("");
                                  }}
                                  className="px-2.5 py-1.5 text-xs font-semibold rounded bg-white border border-rose-200 text-rose-700 hover:bg-rose-50 transition-all"
                                >
                                  Reject
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setDetailModalItem(item)}
                                className="px-2.5 py-1 text-xs font-medium rounded text-stone-600 hover:text-stone-900 hover:bg-stone-100 transition-all"
                              >
                                View Details
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-stone-200 bg-stone-50/50 flex items-center justify-between text-xs">
                <span className="text-stone-500">
                  Showing {(currentPage - 1) * 20 + 1} to{" "}
                  {Math.min(currentPage * 20, totalItems)} of {totalItems}{" "}
                  records
                </span>
                <div className="flex items-center space-x-1">
                  <button
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className="px-2.5 py-1 rounded border border-stone-300 text-stone-700 bg-white hover:bg-stone-50 disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 font-semibold text-stone-700">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    disabled={currentPage >= totalPages}
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    className="px-2.5 py-1 rounded border border-stone-300 text-stone-700 bg-white hover:bg-stone-50 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* APPROVE MODAL */}
      {approveModalItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden border border-stone-200">
            {/* Header */}
            <div className="px-6 py-4 border-b border-stone-200 bg-stone-50/70 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                <h3 className="text-base font-bold text-stone-900">
                  Approve Payout Transfer
                </h3>
              </div>
              <button
                onClick={() => setApproveModalItem(null)}
                className="text-stone-400 hover:text-stone-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4 text-xs text-stone-700">
              <div className="p-4 rounded-lg bg-stone-50 border border-stone-200 space-y-2">
                <div className="flex justify-between items-center text-sm font-semibold">
                  <span className="text-stone-500">Disbursement Amount:</span>
                  <span className="text-base font-bold text-emerald-700">
                    {formatCurrency(approveModalItem.amount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">Buddy:</span>
                  <span className="font-medium text-stone-900">
                    {approveModalItem.provider?.name} (
                    {approveModalItem.provider?.phone})
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">Account Holder:</span>
                  <span className="font-medium text-stone-900">
                    {approveModalItem.payoutAccount?.accountHolderName || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">Bank Account:</span>
                  <span className="font-mono font-medium text-stone-900">
                    {approveModalItem.payoutAccount?.accountNumber || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">IFSC Code:</span>
                  <span className="font-mono font-medium text-stone-900">
                    {approveModalItem.payoutAccount?.ifscCode || "N/A"}
                  </span>
                </div>
              </div>

              <div>
                <label className="block font-semibold text-stone-900 mb-1">
                  Bank UTR / Reference Number{" "}
                  <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={referenceIdInput}
                  onChange={(e) => setReferenceIdInput(e.target.value)}
                  placeholder="e.g. UTR123456789 or CMS-REF-098"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-amber-400 font-mono"
                />
                <span className="text-[11px] text-stone-500 mt-1 block">
                  This transaction ID will be stored and visible to the provider
                  in their buddy application.
                </span>
              </div>

              <div>
                <label className="block font-semibold text-stone-900 mb-1">
                  Admin Internal Notes (Optional)
                </label>
                <input
                  type="text"
                  value={approveNotesInput}
                  onChange={(e) => setApproveNotesInput(e.target.value)}
                  placeholder="e.g. Netbanking IMPS completed"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-amber-400"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3.5 bg-stone-50 border-t border-stone-200 flex items-center justify-end space-x-2">
              <button
                onClick={() => setApproveModalItem(null)}
                className="px-4 py-2 text-xs font-medium rounded-lg text-stone-700 bg-white border border-stone-300 hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmApprove}
                disabled={approving || !referenceIdInput.trim()}
                className="px-4 py-2 text-xs font-semibold rounded-lg text-white bg-stone-900 hover:bg-stone-800 disabled:opacity-50 transition-all flex items-center space-x-1.5"
              >
                {approving ? (
                  <>
                    <svg
                      className="w-3.5 h-3.5 animate-spin"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    <span>Processing...</span>
                  </>
                ) : (
                  <span>Confirm Approval & Transfer</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REJECT MODAL */}
      {rejectModalItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden border border-stone-200">
            {/* Header */}
            <div className="px-6 py-4 border-b border-stone-200 bg-stone-50/70 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500"></span>
                <h3 className="text-base font-bold text-stone-900">
                  Reject Withdrawal & Refund Wallet
                </h3>
              </div>
              <button
                onClick={() => setRejectModalItem(null)}
                className="text-stone-400 hover:text-stone-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4 text-xs text-stone-700">
              <div className="p-3.5 rounded-lg bg-rose-50 border border-rose-200/80 flex items-start space-x-2.5">
                <svg
                  className="w-4 h-4 text-rose-600 shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                <div className="text-[11px] text-rose-800 leading-relaxed">
                  <strong>Automated Refund Guarantee:</strong> When you reject
                  this request,{" "}
                  <strong>{formatCurrency(rejectModalItem.amount)}</strong> will
                  be automatically credited back to{" "}
                  <strong>{rejectModalItem.provider?.name}</strong>'s wallet
                  balance.
                </div>
              </div>

              <div>
                <label className="block font-semibold text-stone-900 mb-1">
                  Reason for Rejection <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={failureReasonInput}
                  onChange={(e) => setFailureReasonInput(e.target.value)}
                  placeholder="e.g. Bank Account IFSC mismatch or Beneficiary Name does not match KYC"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-rose-400"
                />

                {/* Quick Selection Presets */}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {[
                    "Invalid IFSC code",
                    "Account holder name mismatch",
                    "Bank account is frozen or inactive",
                    "Duplicate request",
                  ].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setFailureReasonInput(preset)}
                      className="px-2 py-0.5 rounded text-[10px] bg-stone-100 hover:bg-stone-200 text-stone-700 transition-colors"
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block font-semibold text-stone-900 mb-1">
                  Admin Internal Notes (Optional)
                </label>
                <input
                  type="text"
                  value={rejectNotesInput}
                  onChange={(e) => setRejectNotesInput(e.target.value)}
                  placeholder="e.g. Telephonically verified with buddy"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-rose-400"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3.5 bg-stone-50 border-t border-stone-200 flex items-center justify-end space-x-2">
              <button
                onClick={() => setRejectModalItem(null)}
                className="px-4 py-2 text-xs font-medium rounded-lg text-stone-700 bg-white border border-stone-300 hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReject}
                disabled={rejecting || !failureReasonInput.trim()}
                className="px-4 py-2 text-xs font-semibold rounded-lg text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50 transition-all flex items-center space-x-1.5"
              >
                {rejecting ? (
                  <>
                    <svg
                      className="w-3.5 h-3.5 animate-spin"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    <span>Rejecting & Refunding...</span>
                  </>
                ) : (
                  <span>Confirm Rejection & Refund</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DETAIL / AUDIT MODAL */}
      {detailModalItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden border border-stone-200">
            <div className="px-6 py-4 border-b border-stone-200 bg-stone-50/70 flex items-center justify-between flex-shrink-0">
              <h3 className="text-base font-bold text-stone-900">
                Withdrawal Request Audit Details
              </h3>
              <button
                onClick={() => setDetailModalItem(null)}
                className="text-stone-400 hover:text-stone-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-3 text-xs overflow-y-auto flex-1">
              <div className="flex justify-between py-1.5 border-b border-stone-100">
                <span className="text-stone-500">Request ID:</span>
                <span className="font-mono font-medium text-stone-800">
                  {detailModalItem.id}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-stone-100">
                <span className="text-stone-500">Status:</span>
                <span className="font-bold text-stone-900">
                  {detailModalItem.status}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-stone-100">
                <span className="text-stone-500">Disbursement Amount:</span>
                <span className="font-bold text-stone-900 text-sm">
                  {formatCurrency(detailModalItem.amount)}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-stone-100">
                <span className="text-stone-500">Buddy Name:</span>
                <span className="font-medium text-stone-900">
                  {detailModalItem.provider?.name || "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-stone-100">
                <span className="text-stone-500">Buddy Phone:</span>
                <span className="font-mono text-stone-900">
                  {detailModalItem.provider?.phone || "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-stone-100">
                <span className="text-stone-500">Account Holder:</span>
                <span className="font-medium text-stone-900">
                  {detailModalItem.payoutAccount?.accountHolderName || "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-stone-100">
                <span className="text-stone-500">Account Number:</span>
                <span className="font-mono text-stone-900">
                  {detailModalItem.payoutAccount?.accountNumber || "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-stone-100">
                <span className="text-stone-500">IFSC Code:</span>
                <span className="font-mono text-stone-900">
                  {detailModalItem.payoutAccount?.ifscCode || "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-stone-100">
                <span className="text-stone-500">Requested Timestamp:</span>
                <span className="text-stone-800">
                  {formatDate(detailModalItem.requestedAt)}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-stone-100">
                <span className="text-stone-500">Processed Timestamp:</span>
                <span className="text-stone-800">
                  {formatDate(detailModalItem.processedAt)}
                </span>
              </div>
              {detailModalItem.razorpayReferenceId && (
                <div className="flex justify-between py-1.5 border-b border-stone-100">
                  <span className="text-stone-500">Bank UTR / Ref No:</span>
                  <span className="font-mono font-bold text-emerald-700">
                    {detailModalItem.razorpayReferenceId}
                  </span>
                </div>
              )}
              {detailModalItem.failureReason && (
                <div className="flex justify-between py-1.5 border-b border-stone-100">
                  <span className="text-stone-500">Failure Reason:</span>
                  <span className="font-medium text-rose-600">
                    {detailModalItem.failureReason}
                  </span>
                </div>
              )}
              {detailModalItem.notes && (
                <div className="flex justify-between py-1.5">
                  <span className="text-stone-500">Internal Notes:</span>
                  <span className="text-stone-800">{detailModalItem.notes}</span>
                </div>
              )}
            </div>

            <div className="px-6 py-3.5 bg-stone-50 border-t border-stone-200 flex justify-end flex-shrink-0">
              <button
                onClick={() => setDetailModalItem(null)}
                className="px-4 py-2 text-xs font-semibold rounded-lg text-white bg-stone-900 hover:bg-stone-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payouts;
